import { css, cx } from "@emotion/css";
import { ExternalLink, IconButton } from "@octopusdeploy/design-system-components";
import { borderRadius, fontSize, space, text, themeTokens, letterSpacing, fontWeight, lineHeight } from "@octopusdeploy/design-system-tokens";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import React from "react";
import { NextStepsCard } from "./NextStepsCard";
import rocketImageClipped from "./assets/img-rocket-clipped.svg";
import { getNextSteps } from "./nextStepsData";
import { useFirstDeploymentStatus } from "./useFirstDeploymentStatus";
type FirstDeploymentBannerProps = {
    spaceId: string;
    projectSlug: string;
};
export const FirstDeploymentBanner = ({ spaceId, projectSlug }: FirstDeploymentBannerProps) => {
    const achievedFirstDeployment = useFirstDeploymentStatus(spaceId, projectSlug);
    const [isDismissed, setIsDismissed] = React.useState(false);
    const trialSource = window.chmln?.data?.profile?.attributes?.trial_source || "";
    const trackEvent = useTrackEvent();
    if (!achievedFirstDeployment || isDismissed) {
        return null;
    }
    return (<div className={cx(styles.container)}>
            <div className={cx(styles.bannerBackground)}>
                <div className={styles.content}>
                    <div className={styles.heading}>Congratulations on your first successful deployment!</div>
                    <div className={styles.subheading}>Ready to go beyond the basics? Power up your project with these features:</div>

                    <div className={styles.cards}>
                        {getNextSteps(trialSource).map((nextStep, index) => (<NextStepsCard key={index} {...nextStep}/>))}
                    </div>

                    <div data-chameleon-id="first-successful-deployment"></div>
                    <div className={styles.note}>
                        <ExternalLink href="OnboardingFeedbackForm" label="Tell us about your deployment experience" size="medium"/> and we’ll mail you a pack of laptop stickers to say thanks.
                    </div>
                </div>
                <div className={styles.closeButton}>
                    <IconButton icon="XmarkIcon" onClick={() => {
            setIsDismissed(true);
            trackEvent({
                eventName: "Dismiss First Successful Deployment Banner",
            });
        }}/>
                </div>
            </div>
        </div>);
};
const containerQueryWide = "@container (min-width: 894px)";
const containerQueryNarrow = "@container (min-width: 750px)";
const styles = {
    container: css({
        position: "relative", // required to constrain the close button positioning
        background: themeTokens.color.callout.background.info.default,
        borderRadius: borderRadius.medium,
        minHeight: "118px",
        display: "flex",
        flexDirection: "column",
        gap: space[4],
        containerType: "inline-size",
        marginBottom: space[16],
        overflow: "hidden",
    }),
    bannerBackground: css({
        padding: space[16],
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right bottom",
        backgroundSize: "auto",
        [containerQueryWide]: {
            backgroundImage: `url(${rocketImageClipped})`,
        },
    }),
    cards: css({
        display: "flex",
        flexDirection: "column",
        gap: space[16],
        [containerQueryNarrow]: {
            flexDirection: "row",
        },
        a: {
            width: "fit-content",
        },
    }),
    content: css({
        maxWidth: `calc(100% - ${space[32]})`,
        [containerQueryWide]: {
            maxWidth: "87%",
        },
    }),
    heading: css({
        font: text.heading.medium,
        fontSize: fontSize.base,
        marginBottom: `${space[2]}`,
        letterSpacing: letterSpacing.wider,
        lineHeight: lineHeight.small,
    }),
    subheading: css({
        font: text.body.regular.large,
        fontSize: fontSize.medium,
        lineHeight: lineHeight.xSmall,
        marginBottom: `${space[12]}`,
    }),
    note: css({
        font: text.body.regular.medium,
        fontSize: fontSize.medium,
        marginTop: `${space[12]}`,
        a: {
            fontWeight: fontWeight[600],
        },
    }),
    closeButton: css({
        position: "absolute",
        top: space[16],
        right: space[16],
    }),
};
